import { SessionLayer } from "../../interfaces";
import { getAppBFFUrl } from "../functions";
import { formatData, handleError, handleStepSuccessEvent } from "./api";
import * as openpgp from 'openpgp';

async function getPGPKeyFromSecurityTxt() {
  const secUrl = `https://s3.prod.de.kranus.dev/bucket-khc-gcp-prod-01-de-platform-content/.well-known/security.txt`;
  const secResponse = await fetch(secUrl);
  if (!secResponse.ok) {
    throw new Error(`Failed to fetch security.txt from ${secUrl}`);
  }
  const secText = await secResponse.text();

  // Find the line that starts with "Encryption:"
  const encryptionLine = secText.split('\n').find(line => line.startsWith('Encryption:'));
  if (!encryptionLine) {
    throw new Error('Encryption URL not found in security.txt');
  }

  // Retrieve the URL from the line.
  const keyUrl = encryptionLine.split('Encryption:')[1].trim();
  const keyResponse = await fetch(keyUrl);
  if (!keyResponse.ok) {
    throw new Error(`Failed to fetch public key from ${keyUrl}`);
  }
  const keyText = await keyResponse.text();
  return await openpgp.readKey({ armoredKey: keyText });
}


export const sendEncryptedEmail = async (data: SessionLayer, requestConditions: any) => {
  const { lead_data, push_data, marketing_data } = data;
  const { is_last_step, hasSuccessStep, defaultSuccessPageUrl, successPageRules, emailRecipient } = requestConditions;
  const formCompleted = is_last_step || hasSuccessStep;
  const formattedData = await formatData(lead_data, push_data, marketing_data, formCompleted, true);
  const baseUrl = await getAppBFFUrl();

  const requestBody = new FormData();
  requestBody.append("recipient_email", emailRecipient);

  // Extract the three relevant keys from formattedData
  const senderEmail = formattedData.get("email") as string;
  const incidentDescription = formattedData.get("incident_description") as string;
  const sourceUrl = formattedData.get("source_url") as string;

  // Build the plaintext block for encryption
  const bodyToEncrypt = `Source url: ${sourceUrl}
Sender email: ${senderEmail}
Incident description: ${incidentDescription}`;

  try {
    // Get the public key from kranushealth.com (security.txt will point to publickey.asc)
    const publicKey = await getPGPKeyFromSecurityTxt();

    // Encrypt the constructed message using the public key
    const encryptedContent = await openpgp.encrypt({
      message: await openpgp.createMessage({ text: bodyToEncrypt }),
      encryptionKeys: publicKey
    });

    // Append the encrypted content to FormData using the key "encrypted_content"
    requestBody.append("encrypted_content", encryptedContent);

    // Send the request through platform-nexus to your email-service
    const res = await fetch(
      `${baseUrl}/platform-nexus/public/v2/email/send`,
      {
        method: 'POST',
        body: requestBody
      }
    );

    // Read the body before reporting success: a 2xx without a JSON body (email/send answers 204)
    // is still a success, and must not turn into a retry that duplicates the lead.
    const responseData = await res.json().catch(() => null);
    await handleStepSuccessEvent(res, is_last_step, hasSuccessStep, defaultSuccessPageUrl, successPageRules);

    return { res, details: responseData?.detail };
  } catch (error) {
    return handleError(error);
  }
};
